import React, { useState } from 'react';
import { 
  Crown, ShieldCheck, Sparkles, TrendingUp, Users, Building, 
  Database, Server, Lock, Key, CheckCircle2, AlertTriangle, 
  ArrowRight, Download, RefreshCw, Cpu, HardDrive, Globe, DollarSign,
  GraduationCap, Layers, Activity, FileText, Zap, Shield, ToggleLeft, ToggleRight,
  Sliders, Stethoscope, HeartPulse, Check, AlertCircle, ArrowLeft, LogOut
} from 'lucide-react';
import { UserSession } from '../AppShell';

interface FounderDashboardProps {
  onImpersonateLogin: (user: UserSession) => void;
  onLockOut: () => void;
  onReturnToGateway?: () => void;
  currentUser?: UserSession | null;
  onNavigateToTab?: (tab: string) => void;
  onLaunchModule?: (moduleId: string) => void;
}

export const FounderDashboard: React.FC<FounderDashboardProps> = ({ 
  onImpersonateLogin, 
  onLockOut,
  onReturnToGateway,
  currentUser,
  onNavigateToTab,
  onLaunchModule
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'quick_logins' | 'pricing_engine' | 'state_presentations' | 'tenants' | 'clinical' | 'versions' | 'server' | 'feature_flags'>('overview');
  const [cacheFlushed, setCacheFlushed] = useState(false);
  const [auditExported, setAuditExported] = useState(false);
  const [diagnosticRan, setDiagnosticRan] = useState(false);

  // Dynamic Feature Flags State
  const [featureFlags, setFeatureFlags] = useState({
    aiSoapCopilot: true,
    webcamEyeGaze: true,
    ambientTherapyJukebox: true,
    websocketsRealTimeSync: true,
    teleRehabWebRtc: true,
    ndisAutoClaims: false,
    offlineMeshSync: false
  });

  // Dynamic Tenant Module Provisioning State for demo tenant
  const [demoOrgModules, setDemoOrgModules] = useState<string[]>([
    'aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 
    'student', 'therapist', 'teacher', 'assessment', 'school-erp', 
    'hospital', 'billing', 'communication', 'inventory', 'analytics', 
    'ai', 'sensory-room', 'tele-rehab', 'early-intervention', 'vocational-living', 
    'hydrotherapy', 'music-therapy', 'animal-therapy', 'prosthetics',
    'vbmapp-assessment', 'core-vocab-ai', 'fim-weefim', 'evv-gps-tracker',
    'gamified-neuro', 'transition-matrix', 'audiometer'
  ]);

  const all31ModulesList = [
    'aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 
    'student', 'therapist', 'teacher', 'assessment', 'school-erp', 
    'hospital', 'billing', 'communication', 'inventory', 'analytics', 
    'ai', 'sensory-room', 'tele-rehab', 'early-intervention', 'vocational-living', 
    'hydrotherapy', 'music-therapy', 'animal-therapy', 'prosthetics',
    'vbmapp-assessment', 'core-vocab-ai', 'fim-weefim', 'evv-gps-tracker',
    'gamified-neuro', 'transition-matrix', 'audiometer'
  ];

  const handleFlushCache = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.update();
        }
      }
      localStorage.removeItem('abilityos_cached_version');
      setCacheFlushed(true);
      setTimeout(() => {
        setCacheFlushed(false);
        window.location.reload();
      }, 1200);
    } catch (e) {
      console.warn('Cache flush error:', e);
      setCacheFlushed(true);
      setTimeout(() => setCacheFlushed(false), 3000);
    }
  };

  const handleExportAudit = () => {
    setAuditExported(true);
    setTimeout(() => setAuditExported(false), 3000);
  };

  const handleRunDiagnostic = () => {
    setDiagnosticRan(true);
    setTimeout(() => setDiagnosticRan(false), 3500);
  };

  const toggleFeatureFlag = (key: keyof typeof featureFlags) => {
    setFeatureFlags(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTenantModule = (modId: string) => {
    setDemoOrgModules(prev => 
      prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]
    );
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pt-28 pb-16 px-4 animate-fade-in">
      
      {/* Top Founder Executive Banner */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white p-6 md:p-8 rounded-3xl shadow-2xl border border-amber-500/40 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-72 h-72 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-72 h-72 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-black rounded-full border border-amber-500/40 uppercase tracking-widest">
              <Crown className="w-4 h-4 text-amber-400" />
              <span>Founder &amp; Executive Control Center &bull; Confidential</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              AbilityOS Strategic Command Cockpit
            </h1>
            <p className="text-xs md:text-sm text-slate-300 max-w-3xl leading-relaxed">
              Global executive governance, ARR/MRR commercialization telemetry, 142-tenant module provisioning, clinical RPwD assurance, and multi-version roadmap (v1.0.0 LTS &rarr; v2.5.0 Vision).
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            {onReturnToGateway && (
              <button
                type="button"
                onClick={onReturnToGateway}
                className="px-3.5 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 font-bold rounded-xl text-xs flex items-center gap-2 transition"
                title="Return to Executive 1-Click Login Gateway"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">1-Click Gateway</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleFlushCache}
              className="px-3.5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-bold rounded-xl text-xs flex items-center gap-2 transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${cacheFlushed ? 'animate-spin text-amber-400' : ''}`} />
              <span>{cacheFlushed ? 'Flushed!' : 'Flush Cache'}</span>
            </button>

            <button
              type="button"
              onClick={onLockOut}
              className="px-3.5 py-2.5 bg-red-950/60 hover:bg-red-900 border border-red-500/40 text-red-200 font-bold rounded-xl text-xs flex items-center gap-2 transition"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Lock Cockpit</span>
            </button>
          </div>
        </div>

        {/* Real-Time Executive KPI Metric Ribbons */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6 pt-6 border-t border-slate-800/80">
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Total ARR Run-Rate</div>
            <div className="text-xl font-black text-emerald-400 font-mono mt-0.5">$1.84M</div>
            <div className="text-[10px] text-emerald-500 font-bold">+24.8% YoY</div>
          </div>
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Monthly MRR</div>
            <div className="text-xl font-black text-emerald-300 font-mono mt-0.5">$153.3K</div>
            <div className="text-[10px] text-emerald-400 font-bold">87.4% Margin</div>
          </div>
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Active Tenant Schools</div>
            <div className="text-xl font-black text-white font-mono mt-0.5">142</div>
            <div className="text-[10px] text-blue-400 font-bold">18 In Onboarding</div>
          </div>
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Enrolled Students</div>
            <div className="text-xl font-black text-amber-300 font-mono mt-0.5">28,450</div>
            <div className="text-[10px] text-amber-400 font-bold">94.2% IEP Goals</div>
          </div>
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Production Engine</div>
            <div className="text-xl font-black text-cyan-300 font-mono mt-0.5">PHP 8.5.4</div>
            <div className="text-[10px] text-cyan-400 font-bold">Strict Rule Active</div>
          </div>
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800">
            <div className="text-[10px] uppercase font-bold text-slate-400">Security State</div>
            <div className="text-xl font-black text-emerald-400 font-mono mt-0.5">GUARDED</div>
            <div className="text-[10px] text-emerald-500 font-bold">PIN 3606 Verified</div>
          </div>
        </div>
      </div>

      {/* Cockpit Navigation Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
        <button
          type="button"
          onClick={() => setActiveTab('overview')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'overview' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <span>Overview &amp; Financials</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('quick_logins')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'quick_logins' 
              ? 'bg-amber-500 text-slate-950 shadow-md ring-2 ring-amber-400/40' 
              : 'bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-600" />
          <span>⚡ 1-Click Personas &amp; Demos</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('pricing_engine')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'pricing_engine' 
              ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-md ring-2 ring-[#ffd64e]/40' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Globe className="w-4 h-4 text-[#ffd64e]" />
          <span>💳 Pricing &amp; Multi-Currency Engine</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('state_presentations')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'state_presentations' 
              ? 'bg-emerald-800 text-[#ffd64e] shadow-md ring-2 ring-emerald-400/40' 
              : 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100 border border-emerald-300'
          }`}
        >
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <span>🏛️ State Presentations &amp; Proposals</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('tenants')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'tenants' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Building className="w-4 h-4 text-blue-500" />
          <span>Tenant Provisioning &amp; Licenses</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('clinical')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'clinical' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <HeartPulse className="w-4 h-4 text-rose-500" />
          <span>Clinical Quality &amp; RPwD Matrix</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('versions')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'versions' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Layers className="w-4 h-4 text-purple-500" />
          <span>Roadmap Milestones (v1.0&ndash;v2.5)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('server')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'server' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Server className="w-4 h-4 text-cyan-500" />
          <span>Server Diagnostics (PHP 8.5)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('feature_flags')}
          className={`px-3.5 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
            activeTab === 'feature_flags' 
              ? 'bg-slate-900 text-white shadow-md' 
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Sliders className="w-4 h-4 text-indigo-500" />
          <span>Feature Flags</span>
        </button>
      </div>

      {/* TAB 1: 1-CLICK INSTANT PERSONA LOGINS (IN-DASHBOARD SWITCHER) */}
      {activeTab === 'quick_logins' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-amber-200 shadow-xl space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-900 text-xs font-black rounded-full uppercase tracking-wider mb-1">
                <Lock className="w-3.5 h-3.5 text-amber-600" /> Isolated Executive Access
              </div>
              <h2 className="text-2xl font-black text-slate-900">Founder 1-Click Instant Persona Logins</h2>
              <p className="text-xs text-slate-500">
                These instant switches are completely removed from the public frontend. Click any persona below to immediately switch into that role.
              </p>
            </div>
            <div className="text-xs font-mono bg-slate-900 text-amber-300 px-3 py-1.5 rounded-xl self-start md:self-auto font-bold">
              GATEWAY: /founder [FOUNDER VERIFIED]
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            
            {/* Founder Self Persona */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-2 border-amber-300 p-5 rounded-2xl shadow-sm space-y-3 flex flex-col justify-between hover:shadow-md transition">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-amber-500 text-slate-950 rounded-xl flex items-center justify-center font-black shadow">
                  <Crown className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-base">Founder &amp; CEO</h3>
                <p className="text-xs text-slate-600 leading-snug">
                  Active Founder session with omni-access, financial KPIs, and global executive oversight.
                </p>
                <div className="text-[11px] font-mono text-amber-950 font-bold bg-white/80 p-1.5 rounded-lg border border-amber-300">
                  founder@bugfreesolution.com
                </div>
              </div>

              <button
                type="button"
                onClick={() => onImpersonateLogin({
                  name: 'Digvijay Yenurkar (Founder & CEO)',
                  email: 'founder@bugfreesolution.com',
                  role: 'FOUNDER',
                  tenant: 'AbilityOS Global Holdings & Founder Cockpit',
                  subscriptionActive: true,
                  enabledModules: all31ModulesList
                })}
                className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs rounded-xl shadow transition flex items-center justify-center gap-1.5"
              >
                <span>Active as Founder</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-slate-950" />
              </button>
            </div>

            {/* Super Admin */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 p-5 rounded-2xl shadow-sm space-y-3 flex flex-col justify-between hover:shadow-md transition">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center font-bold shadow">
                  <Shield className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-base">Super Admin (Root)</h3>
                <p className="text-xs text-slate-600 leading-snug">
                  Full WHM server control, cross-tenant database syncing, and system infrastructure.
                </p>
                <div className="text-[11px] font-mono text-emerald-800 font-bold bg-white/70 p-1.5 rounded-lg border border-emerald-200">
                  superadmin@bugfreesolution.com
                </div>
              </div>

              <button
                type="button"
                onClick={() => onImpersonateLogin({
                  name: 'Global Root Administrator',
                  email: 'superadmin@bugfreesolution.com',
                  role: 'SUPER_ADMIN',
                  tenant: 'WHM / cPanel MultiPHP 8.5 Root',
                  subscriptionActive: true,
                  enabledModules: all31ModulesList
                })}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl shadow transition flex items-center justify-center gap-1.5"
              >
                <span>Launch as Super Admin</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* School / Tenant Admin */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 p-5 rounded-2xl shadow-sm space-y-3 flex flex-col justify-between hover:shadow-md transition">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold shadow">
                  <Building className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-base">School Admin (Demo)</h3>
                <p className="text-xs text-slate-600 leading-snug">
                  Special school administrator with student enrollment, therapist rosters, and NDIS billing claims.
                </p>
                <div className="text-[11px] font-mono text-blue-800 font-bold bg-white/70 p-1.5 rounded-lg border border-blue-200">
                  demo@bugfreesolution.com
                </div>
              </div>

              <button
                type="button"
                onClick={() => onImpersonateLogin({
                  name: 'Demo Admin User',
                  email: 'demo@bugfreesolution.com',
                  role: 'TENANT_ADMIN',
                  tenant: 'BugFree Solutions Demo Organization',
                  subscriptionActive: true,
                  enabledModules: all31ModulesList
                })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs rounded-xl shadow transition flex items-center justify-center gap-1.5"
              >
                <span>Launch as School Admin</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Lead Clinical Therapist */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 p-5 rounded-2xl shadow-sm space-y-3 flex flex-col justify-between hover:shadow-md transition">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-purple-600 text-white rounded-xl flex items-center justify-center font-bold shadow">
                  <Users className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-base">Senior Therapist</h3>
                <p className="text-xs text-slate-600 leading-snug">
                  Clinical workspace for SOAP progress notes, articulation therapy, and IEP milestone evaluation.
                </p>
                <div className="text-[11px] font-mono text-purple-800 font-bold bg-white/70 p-1.5 rounded-lg border border-purple-200">
                  therapist@bugfreesolution.com
                </div>
              </div>

              <button
                type="button"
                onClick={() => onImpersonateLogin({
                  name: 'Sarah Jenkins (SLP)',
                  email: 'therapist@bugfreesolution.com',
                  role: 'THERAPIST',
                  tenant: 'BugFree Special School',
                  subscriptionActive: true,
                  enabledModules: all31ModulesList
                })}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-black text-xs rounded-xl shadow transition flex items-center justify-center gap-1.5"
              >
                <span>Launch as Lead Therapist</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Student Profile */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 p-5 rounded-2xl shadow-sm space-y-3 flex flex-col justify-between hover:shadow-md transition">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-amber-500 text-slate-950 rounded-xl flex items-center justify-center font-bold shadow">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-base">Student Portal</h3>
                <p className="text-xs text-slate-600 leading-snug">
                  Inclusive learner view with Text-to-Speech audio reader, mood check-in, token star rewards, and accommodations.
                </p>
                <div className="text-[11px] font-mono text-amber-900 font-bold bg-white/70 p-1.5 rounded-lg border border-amber-200">
                  student@bugfreesolution.com
                </div>
              </div>

              <button
                type="button"
                onClick={() => onImpersonateLogin({
                  name: 'Master Rahul Sharma',
                  email: 'student@bugfreesolution.com',
                  role: 'STUDENT',
                  tenant: 'BugFree Special School',
                  subscriptionActive: true,
                  enabledModules: all31ModulesList
                })}
                className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs rounded-xl shadow transition flex items-center justify-center gap-1.5"
              >
                <span>Launch as Student</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

          </div>

            {/* DIRECT CLINICAL MODULE DEMO LAUNCHERS */}
            <div className="pt-6 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div>
                  <h3 className="font-black text-slate-900 text-lg flex items-center gap-2">
                    <Stethoscope className="w-5 h-5 text-blue-600" />
                    <span>Founder 1-Click Interactive Clinical Module Demo Launchers</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Directly launch newly developed flagship clinical tools in interactive demo mode with preloaded mock diagnostic data.
                  </p>
                </div>
                <span className="text-[11px] font-mono font-black text-amber-900 bg-amber-100 px-3 py-1 rounded-xl self-start sm:self-auto border border-amber-300">
                  ALL 31 MODULES READY
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                
                {/* 1. Diagnostic Audiometer */}
                <div className="bg-slate-50 border border-slate-200 hover:border-blue-400 p-4 rounded-2xl flex flex-col justify-between transition group hover:shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                        Hearing &amp; Audiology
                      </span>
                      <span className="text-[10px] font-mono text-emerald-600 font-bold">ISO/ANSI S3.6</span>
                    </div>
                    <h4 className="font-black text-slate-900 text-sm">Diagnostic Pure-Tone Audiometer</h4>
                    <p className="text-xs text-slate-500 leading-snug">
                      Binaural 125-8000Hz pure-tone audiogram with Speech Banana overlay and PTA severity classifier.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (onLaunchModule) onLaunchModule('audiometer');
                      else if (onNavigateToTab) onNavigateToTab('disabilities');
                    }}
                    className="mt-3 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Launch Audiometer Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 2. Dr. Speech Studio */}
                <div className="bg-slate-50 border border-slate-200 hover:border-amber-400 p-4 rounded-2xl flex flex-col justify-between transition group hover:shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                        Speech Diagnostics
                      </span>
                      <span className="text-[10px] font-mono text-emerald-600 font-bold">Tiger DRS Equiv</span>
                    </div>
                    <h4 className="font-black text-slate-900 text-sm">Dr. Speech Studio FFT &amp; MPT</h4>
                    <p className="text-xs text-slate-500 leading-snug">
                      Dual WebAudio FFT spectrum, Maximum Phonation Time stopwatch, and Voice Balloon elevation game.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (onLaunchModule) onLaunchModule('speech-therapy');
                      else if (onNavigateToTab) onNavigateToTab('disabilities');
                    }}
                    className="mt-3 w-full py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Launch Dr. Speech Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 3. Core AAC Board */}
                <div className="bg-slate-50 border border-slate-200 hover:border-emerald-400 p-4 rounded-2xl flex flex-col justify-between transition group hover:shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                        Assistive AAC
                      </span>
                      <span className="text-[10px] font-mono text-blue-600 font-bold">Fitzgerald Key</span>
                    </div>
                    <h4 className="font-black text-slate-900 text-sm">Core AAC Board &amp; Voice Engine</h4>
                    <p className="text-xs text-slate-500 leading-snug">
                      500+ vocabulary tiles, multilingual TTS (EN, HI, MR), custom photos, and CP tremor dwell filter.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigateToTab && onNavigateToTab('aac')}
                    className="mt-3 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Launch Core AAC Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 4. EVV GPS & NDIS Billing */}
                <div className="bg-slate-50 border border-slate-200 hover:border-purple-400 p-4 rounded-2xl flex flex-col justify-between transition group hover:shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-purple-800 bg-purple-100 px-2 py-0.5 rounded">
                        Field Care &amp; ERP
                      </span>
                      <span className="text-[10px] font-mono text-purple-600 font-bold">21st Century Cures</span>
                    </div>
                    <h4 className="font-black text-slate-900 text-sm">EVV GPS Tracker &amp; Billing</h4>
                    <p className="text-xs text-slate-500 leading-snug">
                      Geofenced mobile clinician visit verification, timestamps, and automated NDIS/UDID claims.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (onLaunchModule) onLaunchModule('evv-gps-tracker');
                      else if (onNavigateToTab) onNavigateToTab('disabilities');
                    }}
                    className="mt-3 w-full py-2 bg-purple-600 hover:bg-purple-700 text-white font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Launch EVV GPS Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 5. 1-Click Official IEP PDF Exporter */}
                <div className="bg-slate-50 border border-slate-200 hover:border-rose-400 p-4 rounded-2xl flex flex-col justify-between transition group hover:shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-rose-800 bg-rose-100 px-2 py-0.5 rounded">
                        Special Ed IEP
                      </span>
                      <span className="text-[10px] font-mono text-rose-600 font-bold">IDEA / RPwD</span>
                    </div>
                    <h4 className="font-black text-slate-900 text-sm">1-Click Official IEP Exporter</h4>
                    <p className="text-xs text-slate-500 leading-snug">
                      Official standardized IEP PDF document generation with goal rubrics, accommodations, and signatures.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (onLaunchModule) onLaunchModule('student');
                      else if (onNavigateToTab) onNavigateToTab('disabilities');
                    }}
                    className="mt-3 w-full py-2 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Launch IEP Exporter Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 6. Multi-Currency Pricing Engine Preview */}
                <div className="bg-slate-900 text-white border border-[#ffd64e]/40 p-4 rounded-2xl flex flex-col justify-between transition group shadow-md">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-black bg-[#ffd64e] px-2 py-0.5 rounded">
                        Commercial Engine
                      </span>
                      <span className="text-[10px] font-mono text-[#ffd64e] font-bold">5 Currencies</span>
                    </div>
                    <h4 className="font-black text-white text-sm">Standalone Pricing Page (/pricing)</h4>
                    <p className="text-xs text-slate-300 leading-snug">
                      Intelligent country auto-detection (INR ₹ 70% PPP, USD, EUR, GBP, AUD) and interactive TCO calculator.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigateToTab && onNavigateToTab('pricing')}
                    className="mt-3 w-full py-2 bg-[#ffd64e] hover:bg-[#f5cb3a] text-black font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    <span>Open Live /pricing Page</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 7. Madhya Pradesh State Presentation & Proposal */}
                <div className="bg-emerald-950 text-white border border-emerald-500/40 p-4 rounded-2xl flex flex-col justify-between transition group shadow-md hover:border-emerald-400">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-slate-950 bg-emerald-400 px-2 py-0.5 rounded">
                        State Proposal
                      </span>
                      <span className="text-[10px] font-mono text-emerald-300 font-bold">52 DDRCs &bull; 10 Slides</span>
                    </div>
                    <h4 className="font-black text-white text-sm">Madhya Pradesh State Deck</h4>
                    <p className="text-xs text-slate-300 leading-snug">
                      Rajya Shiksha Kendra, Samagra Shiksha MP, and 52 DDRC cloud transformation proposal.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigateToTab && onNavigateToTab('madhya_pradesh')}
                    className="mt-3 w-full py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Open State Presentation</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

              </div>
            </div>

        </div>
      )}

      {/* TAB: PRICING & MULTI-CURRENCY ENGINE */}
      {activeTab === 'pricing_engine' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-xl space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#ffd64e]/20 text-black text-xs font-black rounded-full uppercase tracking-wider mb-1 border border-[#ffd64e]/40">
                <Globe className="w-3.5 h-3.5 text-black" /> Commercialization &amp; Global Access
              </div>
              <h2 className="text-2xl font-black text-slate-900">Multi-Currency &amp; Purchasing Power Parity (PPP) Engine</h2>
              <p className="text-xs text-slate-500">
                Real-time governance of global subscription tiers, localized currency detection, and statutory discount rubrics.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateToTab && onNavigateToTab('pricing')}
              className="px-4 py-2 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] font-black text-xs rounded-xl flex items-center gap-2 transition self-start md:self-auto shadow"
            >
              <span>View Live /pricing Page</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            
            {/* India INR */}
            <div className="bg-amber-50/60 border-2 border-amber-300 p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl">🇮🇳</span>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-full uppercase">
                  70% PPP Active
                </span>
              </div>
              <div>
                <h4 className="font-black text-slate-900 text-sm">India &amp; South Asia (INR ₹)</h4>
                <p className="text-[11px] text-slate-600">RPwD Act 2016 inclusive pricing</p>
              </div>
              <div className="text-xs space-y-1 pt-2 border-t border-amber-200 font-mono">
                <div className="flex justify-between"><span>Community:</span> <span className="font-bold">₹0</span></div>
                <div className="flex justify-between"><span>Family Pro:</span> <span className="font-bold">₹299/mo</span></div>
                <div className="flex justify-between"><span>Clinician Pro:</span> <span className="font-bold">₹1,499/mo</span></div>
                <div className="flex justify-between"><span>School ERP:</span> <span className="font-bold">₹6,999/mo</span></div>
              </div>
            </div>

            {/* USA USD */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl">🇺🇸</span>
                <span className="px-2 py-0.5 bg-slate-200 text-slate-800 text-[10px] font-black rounded-full uppercase">
                  Standard Global
                </span>
              </div>
              <div>
                <h4 className="font-black text-slate-900 text-sm">United States &amp; Global (USD $)</h4>
                <p className="text-[11px] text-slate-600">Standard commercial rate</p>
              </div>
              <div className="text-xs space-y-1 pt-2 border-t border-slate-200 font-mono">
                <div className="flex justify-between"><span>Community:</span> <span className="font-bold">$0</span></div>
                <div className="flex justify-between"><span>Family Pro:</span> <span className="font-bold">$9.99/mo</span></div>
                <div className="flex justify-between"><span>Clinician Pro:</span> <span className="font-bold">$39/mo</span></div>
                <div className="flex justify-between"><span>School ERP:</span> <span className="font-bold">$149/mo</span></div>
              </div>
            </div>

            {/* Eurozone EUR */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl">🇪🇺</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-black rounded-full uppercase">
                  EU VAT Included
                </span>
              </div>
              <div>
                <h4 className="font-black text-slate-900 text-sm">Eurozone (EUR €)</h4>
                <p className="text-[11px] text-slate-600">European clinical compliance</p>
              </div>
              <div className="text-xs space-y-1 pt-2 border-t border-slate-200 font-mono">
                <div className="flex justify-between"><span>Community:</span> <span className="font-bold">€0</span></div>
                <div className="flex justify-between"><span>Family Pro:</span> <span className="font-bold">€8.99/mo</span></div>
                <div className="flex justify-between"><span>Clinician Pro:</span> <span className="font-bold">€35/mo</span></div>
                <div className="flex justify-between"><span>School ERP:</span> <span className="font-bold">€139/mo</span></div>
              </div>
            </div>

            {/* UK GBP */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl">🇬🇧</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-black rounded-full uppercase">
                  NHS Tier
                </span>
              </div>
              <div>
                <h4 className="font-black text-slate-900 text-sm">United Kingdom (GBP £)</h4>
                <p className="text-[11px] text-slate-600">NHS Trusts &amp; SEND schools</p>
              </div>
              <div className="text-xs space-y-1 pt-2 border-t border-slate-200 font-mono">
                <div className="flex justify-between"><span>Community:</span> <span className="font-bold">£0</span></div>
                <div className="flex justify-between"><span>Family Pro:</span> <span className="font-bold">£7.99/mo</span></div>
                <div className="flex justify-between"><span>Clinician Pro:</span> <span className="font-bold">£29/mo</span></div>
                <div className="flex justify-between"><span>School ERP:</span> <span className="font-bold">£119/mo</span></div>
              </div>
            </div>

            {/* Australia AUD */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl">🇦🇺</span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-black rounded-full uppercase">
                  NDIS Tier
                </span>
              </div>
              <div>
                <h4 className="font-black text-slate-900 text-sm">Australia (AUD A$)</h4>
                <p className="text-[11px] text-slate-600">NDIS registered provider rates</p>
              </div>
              <div className="text-xs space-y-1 pt-2 border-t border-slate-200 font-mono">
                <div className="flex justify-between"><span>Community:</span> <span className="font-bold">A$0</span></div>
                <div className="flex justify-between"><span>Family Pro:</span> <span className="font-bold">A$14.99/mo</span></div>
                <div className="flex justify-between"><span>Clinician Pro:</span> <span className="font-bold">A$59/mo</span></div>
                <div className="flex justify-between"><span>School ERP:</span> <span className="font-bold">A$219/mo</span></div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* TAB: STATE PRESENTATIONS & PROPOSALS */}
      {activeTab === 'state_presentations' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-emerald-200 shadow-xl space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-900 text-xs font-black rounded-full uppercase tracking-wider mb-1 border border-emerald-300">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Government Relations &amp; State Proposals
              </div>
              <h2 className="text-2xl font-black text-slate-900">Statewide Transformation Presentations &amp; Share Links</h2>
              <p className="text-xs text-slate-500">
                Official presentation decks for state ministries, Rajya Shiksha Kendra, DDRCs, and district administrations with live sharing links.
              </p>
            </div>
            
            <button
              type="button"
              onClick={() => onNavigateToTab && onNavigateToTab('madhya_pradesh')}
              className="px-4 py-2.5 bg-slate-900 hover:bg-black text-[#ffd64e] font-black text-xs rounded-xl flex items-center gap-2 transition self-start md:self-auto shadow-md"
            >
              <span>Launch MP Keynote Deck</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Dedicated State Proposal Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Card 1: Madhya Pradesh State Transformation Deck */}
            <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white rounded-3xl p-6 sm:p-7 border border-emerald-500/40 shadow-xl flex flex-col justify-between space-y-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-60 h-60 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="space-y-3 relative z-10">
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 rounded-full bg-[#ffd64e] text-slate-950 font-black font-mono text-xs uppercase tracking-wider">
                    Official Active Proposal
                  </span>
                  <span className="text-xs font-mono text-emerald-400 font-bold">10 Keynote Slides</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  Madhya Pradesh Universal Special Education &amp; Rehabilitation Cloud
                </h3>
                
                <p className="text-xs text-slate-300 leading-relaxed">
                  Tailored for Rajya Shiksha Kendra (Samagra Shiksha MP), Directorate of Social Justice &amp; PwD Empowerment, 52 District DDRCs, and CM Rise Inclusive Schools.
                </p>

                <div className="grid grid-cols-3 gap-2 pt-2 text-center">
                  <div className="p-2 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-base font-black text-[#ffd64e] font-mono">52</div>
                    <div className="text-[10px] text-slate-300 font-mono">DDRC Nodes</div>
                  </div>
                  <div className="p-2 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-base font-black text-[#ffd64e] font-mono">1.5L+</div>
                    <div className="text-[10px] text-slate-300 font-mono">CwSN Students</div>
                  </div>
                  <div className="p-2 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-base font-black text-emerald-400 font-mono">₹18.5 Cr</div>
                    <div className="text-[10px] text-slate-300 font-mono">CapEx Saved</div>
                  </div>
                </div>

                {/* Shareable URL Links Box */}
                <div className="p-3 rounded-2xl bg-black/40 border border-white/10 space-y-2">
                  <div className="text-[11px] font-mono text-slate-400 flex items-center justify-between">
                    <span>Public Presentation Share URL:</span>
                    <span className="text-emerald-400 font-bold">Live &bull; HTTP 200 OK</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value="https://abilityos.bugfreesolution.com/madhya-pradesh"
                      className="w-full px-3 py-1.5 bg-black/60 border border-white/15 rounded-xl text-xs font-mono text-[#ffd64e] select-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText('https://abilityos.bugfreesolution.com/madhya-pradesh');
                        alert('Madhya Pradesh presentation link copied to clipboard!');
                      }}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white font-mono text-xs font-bold rounded-xl transition shrink-0 cursor-pointer"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-2 relative z-10 flex-wrap">
                <button
                  type="button"
                  onClick={() => onNavigateToTab && onNavigateToTab('madhya_pradesh')}
                  className="px-4 py-2.5 bg-[#ffd64e] hover:bg-[#ffe27a] text-black font-black text-xs rounded-xl transition flex items-center gap-2 cursor-pointer shadow-md"
                >
                  <span>Open Keynote Presentation</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <a
                  href="https://abilityos.bugfreesolution.com/madhya-pradesh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5"
                >
                  <span>Open in New Window</span>
                  <ArrowRight className="w-3.5 h-3.5 -rotate-45" />
                </a>
              </div>
            </div>

            {/* Card 2: National & Multi-State Reusability Blueprint */}
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-7 flex flex-col justify-between space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-800 font-black font-mono text-xs uppercase tracking-wider">
                    Multi-State Template
                  </span>
                  <span className="text-xs font-mono text-slate-500 font-bold">Standard Architecture</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  State Education &amp; Rehabilitation Deployment Framework
                </h3>

                <p className="text-xs text-slate-600 leading-relaxed">
                  The Madhya Pradesh blueprint is structured as a re-usable turnkey proposal architecture that can be deployed across other Indian states (Maharashtra, Uttar Pradesh, Rajasthan, Gujarat) or international ministries.
                </p>

                <div className="space-y-2 pt-2 text-xs">
                  <div className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                    <span className="font-bold text-slate-800">All 31 Clinical Modules Included:</span>
                    <span className="font-mono text-emerald-600 font-bold">100% Full Access</span>
                  </div>
                  <div className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                    <span className="font-bold text-slate-800">Hardware-Free CapEx Reduction:</span>
                    <span className="font-mono text-emerald-600 font-bold">&gt;85% Savings</span>
                  </div>
                  <div className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                    <span className="font-bold text-slate-800">Offline Tribal Mode (Dexie.js):</span>
                    <span className="font-mono text-emerald-600 font-bold">Zero-Network Ready</span>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs">
                <span className="text-slate-500 font-mono">Status: Ready for Statewide Tenders</span>
                <button
                  type="button"
                  onClick={() => onNavigateToTab && onNavigateToTab('request_demo')}
                  className="px-4 py-2 bg-slate-900 hover:bg-black text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer"
                >
                  <span>Initiate New State Pilot</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* TAB 2: EXECUTIVE OVERVIEW & FINANCIALS */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Revenue breakdown */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900 flex items-center gap-2 text-base">
                <DollarSign className="w-5 h-5 text-emerald-600" /> SaaS Revenue &amp; Commercialization
              </h3>
              <div className="space-y-3 text-xs">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Enterprise School Licenses</span>
                  <span className="font-black text-slate-900 font-mono">$1,120,000 / yr</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">NDIS Clinical Provider Tiers</span>
                  <span className="font-black text-slate-900 font-mono">$480,000 / yr</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Hospital Inpatient Rehab Tiers</span>
                  <span className="font-black text-slate-900 font-mono">$240,000 / yr</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Gross Operating Margin</span>
                  <span className="font-black text-emerald-600 font-mono">87.4%</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Net Revenue Retention (NRR)</span>
                  <span className="font-black text-emerald-600 font-mono">118%</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Customer Churn Rate</span>
                  <span className="font-black text-emerald-600 font-mono">&lt; 0.8%</span>
                </div>
              </div>
            </div>

            {/* Compliance & Regulatory Posture */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900 flex items-center gap-2 text-base">
                <ShieldCheck className="w-5 h-5 text-blue-600" /> Regulatory &amp; Standard Compliance
              </h3>
              <div className="space-y-2.5 text-xs">
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <span className="font-bold text-slate-800">RPwD Act 2016 (India)</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-black rounded text-[10px]">21/21 Validated</span>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <span className="font-bold text-slate-800">NEP 2020 Inclusive Framework</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-black rounded text-[10px]">Certified</span>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <span className="font-bold text-slate-800">NDIS Quality &amp; Safeguards</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-black rounded text-[10px]">Active</span>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <span className="font-bold text-slate-800">WHO ICF-CY Classification</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-black rounded text-[10px]">Mapped</span>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <span className="font-bold text-slate-800">HIPAA &amp; FERPA Encryption</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-black rounded text-[10px]">AES-256</span>
                </div>
              </div>
            </div>

            {/* Quick Founder Actions */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900 flex items-center gap-2 text-base">
                <Zap className="w-5 h-5 text-amber-500" /> Founder Quick Commands
              </h3>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleExportAudit}
                  className="w-full p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl font-bold text-xs text-slate-700 flex items-center justify-between transition"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span>{auditExported ? 'Audit Log Exported (CSV)!' : 'Export Global Audit Log (CSV)'}</span>
                  </span>
                  <Download className="w-3.5 h-3.5" />
                </button>

                <button
                  type="button"
                  onClick={handleRunDiagnostic}
                  className="w-full p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl font-bold text-xs text-slate-700 flex items-center justify-between transition"
                >
                  <span className="flex items-center gap-2">
                    <Activity className={`w-4 h-4 text-emerald-600 ${diagnosticRan ? 'animate-spin' : ''}`} />
                    <span>{diagnosticRan ? 'Diagnostic Passed: 30 Modules OK' : 'Run Clinical Integrity Diagnostic'}</span>
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('quick_logins')}
                  className="w-full p-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl font-black text-xs flex items-center justify-between shadow transition"
                >
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>Launch 1-Click Login Cockpit</span>
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* TAB 3: TENANT ORGANIZATIONS & 30-MODULE PROVISIONING */}
      {activeTab === 'tenants' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-xl font-black text-slate-900">Tenant Organizations Directory</h2>
                <p className="text-xs text-slate-500">Live multi-tenant provisioning with license keys and seat allocations</p>
              </div>
              <span className="text-xs font-mono font-bold bg-blue-50 text-blue-700 px-3 py-1 rounded-xl self-start sm:self-auto">
                142 Organizations Registered &bull; 18 In Onboarding
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase font-black tracking-wider text-[10px]">
                  <tr>
                    <th className="p-3">Organization</th>
                    <th className="p-3">Admin Email</th>
                    <th className="p-3">Plan Tier</th>
                    <th className="p-3">Seats</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900">BugFree Solutions Demo Organization</td>
                    <td className="p-3 font-mono text-slate-600">demo@bugfreesolution.com</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-amber-100 text-amber-900 rounded font-black text-[10px]">ENTERPRISE_30_SUITE</span></td>
                    <td className="p-3 font-mono font-bold">175 Students</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">ACTIVE</span></td>
                    <td className="p-3 text-right">
                      <button 
                        type="button"
                        onClick={() => onImpersonateLogin({
                          name: 'Demo Admin User',
                          email: 'demo@bugfreesolution.com',
                          role: 'TENANT_ADMIN',
                          tenant: 'BugFree Solutions Demo Organization',
                          subscriptionActive: true,
                          enabledModules: demoOrgModules
                        })}
                        className="px-2.5 py-1 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700"
                      >
                        Enter Tenant
                      </button>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900">St. Jude Special School</td>
                    <td className="p-3 font-mono text-slate-600">principal@stjude.edu</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded font-black text-[10px]">ENTERPRISE_SCHOOL</span></td>
                    <td className="p-3 font-mono font-bold">142 Students</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">ACTIVE</span></td>
                    <td className="p-3 text-right">
                      <button type="button" className="px-2.5 py-1 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200">
                        Manage
                      </button>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900">Sunshine Therapy Centre</td>
                    <td className="p-3 font-mono text-slate-600">admin@sunshinetherapy.org</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-purple-100 text-purple-900 rounded font-black text-[10px]">PRO_CLINIC</span></td>
                    <td className="p-3 font-mono font-bold">88 Students</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">ACTIVE</span></td>
                    <td className="p-3 text-right">
                      <button type="button" className="px-2.5 py-1 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200">
                        Manage
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Dynamic 31-Module Entitlement Switcher for Demo Organization */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="font-black text-slate-900 text-base">
                  Live 31-Module Provisioning Control (BugFree Solutions Demo Organization)
                </h3>
                <p className="text-xs text-slate-500">
                  Toggle individual module entitlements for this tenant. Currently active: <span className="font-bold text-blue-600">{demoOrgModules.length} / 31 modules</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDemoOrgModules(all31ModulesList)}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl self-start sm:self-auto"
              >
                Enable All 31 Modules
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 pt-2">
              {all31ModulesList.map((modId) => {
                const isEnabled = demoOrgModules.includes(modId);
                return (
                  <button
                    key={modId}
                    type="button"
                    onClick={() => toggleTenantModule(modId)}
                    className={`p-2.5 rounded-xl border text-left transition flex flex-col justify-between ${
                      isEnabled 
                        ? 'bg-blue-50 border-blue-300 text-blue-900 shadow-sm' 
                        : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                    }`}
                  >
                    <span className="font-bold text-[11px] truncate capitalize">
                      {modId.replace(/-/g, ' ')}
                    </span>
                    <span className={`text-[9px] font-black uppercase mt-1 ${isEnabled ? 'text-blue-700' : 'text-slate-400'}`}>
                      {isEnabled ? '● Active' : '○ Disabled'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: CLINICAL QUALITY & RPwD MATRIX */}
      {activeTab === 'clinical' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-black rounded-full uppercase tracking-wider">
                  Clinical Quality Assurance
                </span>
                <h2 className="text-xl font-black text-slate-900 mt-2">
                  RPwD Act 2016 21-Disability Statutory Mapping &amp; Efficacy
                </h2>
                <p className="text-xs text-slate-500">
                  Real-time outcome telemetry across 28,450 enrolled special education students.
                </p>
              </div>
              <span className="text-xs font-mono font-bold bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-xl self-start sm:self-auto">
                100% STATUTORY COVERAGE (21/21)
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <div className="text-[10px] text-slate-500 font-bold uppercase">Total Therapy Sessions</div>
                <div className="text-2xl font-black text-slate-900 font-mono">126,840</div>
                <div className="text-[10px] text-emerald-600 font-bold">Speech, OT, PT, Behavioral</div>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <div className="text-[10px] text-slate-500 font-bold uppercase">IEP Goal Attainment Rate</div>
                <div className="text-2xl font-black text-emerald-600 font-mono">94.2%</div>
                <div className="text-[10px] text-emerald-700 font-bold">Standardized Rubrics Met</div>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <div className="text-[10px] text-slate-500 font-bold uppercase">AAC Speech Utterances</div>
                <div className="text-2xl font-black text-blue-600 font-mono">4.12M</div>
                <div className="text-[10px] text-blue-700 font-bold">EN / HI / MR Voices</div>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <div className="text-[10px] text-slate-500 font-bold uppercase">Clinical Adverse Events</div>
                <div className="text-2xl font-black text-emerald-600 font-mono">0</div>
                <div className="text-[10px] text-emerald-700 font-bold">100% Zero-Harm Record</div>
              </div>
            </div>

            {/* 8 Clinical Disability Clusters */}
            <div className="pt-4 border-t border-slate-100">
              <h4 className="text-xs font-black uppercase text-slate-700 mb-3">8 Clinical Disability Clusters Verified</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                {[
                  { name: 'Autism & Sensory Processing', code: 'RPwD #05 / DSM-5 299.00', count: '8,420 students', ok: true },
                  { name: 'Speech, Language & AAC', code: 'RPwD #12 / DSM-5 315.39', count: '6,150 students', ok: true },
                  { name: 'Locomotor & Cerebral Palsy', code: 'RPwD #03 / ICD-11 8D40', count: '4,890 students', ok: true },
                  { name: 'Neurological & Brain Injury', code: 'RPwD #18 / ICD-11 8E4A', count: '2,940 students', ok: true },
                  { name: 'Visual Impairment & Braille', code: 'RPwD #01 / ICD-11 9D00', count: '1,830 students', ok: true },
                  { name: 'Hearing Impairment & ISL', code: 'RPwD #02 / ICD-11 AB50', count: '2,120 students', ok: true },
                  { name: 'Specific Learning Disabilities', code: 'RPwD #14 / DSM-5 315.00', count: '1,560 students', ok: true },
                  { name: 'Vocational & Transition Living', code: 'RPwD #04 / WHO ICF-CY', count: '540 students', ok: true },
                ].map((item, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{item.name}</span>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <div className="text-[10px] font-mono text-slate-500">{item.code}</div>
                    <div className="text-[10px] font-bold text-blue-700">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: VERSION MATRIX & ROADMAP (v1.0 to v2.5) */}
      {activeTab === 'versions' && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6">
          <div>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-black rounded-full uppercase tracking-wider">
              Product Roadmap &amp; Semantic Version Registry
            </span>
            <h2 className="text-2xl font-black text-slate-900 mt-2">AbilityOS Multi-Version Evolution</h2>
            <p className="text-xs text-slate-500">Comprehensive overview of released, current, and upcoming releases across the AbilityOS platform.</p>
          </div>

          <div className="space-y-4">
            
            {/* Version 1.0 */}
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-slate-800 text-white font-mono font-bold text-xs rounded-lg">v1.0.0 LTS</span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">RELEASED</span>
                </div>
                <h4 className="font-black text-slate-900 text-sm">Initial Enterprise 30-Module Suite &amp; Offline PWA</h4>
                <p className="text-xs text-slate-500">
                  Full rollout of 30 specialized therapy modules, 96 cross-platform native installers (Android APK, Windows MSIX, macOS PKG), and multi-tenant data architecture.
                </p>
              </div>
              <div className="text-xs font-mono text-slate-500 font-bold shrink-0">Released: Q1 2026</div>
            </div>

            {/* Version 1.2 */}
            <div className="p-5 bg-emerald-50 border-2 border-emerald-300 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-emerald-600 text-white font-mono font-bold text-xs rounded-lg">v1.2.0 (CURRENT)</span>
                  <span className="text-xs font-bold text-emerald-800 bg-emerald-200 px-2 py-0.5 rounded">LIVE PRODUCTION</span>
                </div>
                <h4 className="font-black text-slate-900 text-sm">Standalone Pricing Page, Multi-Currency PPP Engine, Diagnostic Audiometer &amp; Dr. Speech Studio</h4>
                <p className="text-xs text-slate-600">
                  Dedicated /pricing page with intelligent country/timezone auto-detection, ISO/ANSI S3.6 Pure-Tone Audiometer &amp; Audiogram, Dr. Speech Studio real-time FFT &amp; MPT, Clarvos front-end redesign (Hero, Comparison Chart, Global Footer), and public demo isolation behind Founder PIN 3606. Strict PHP 8.5 backend requirement active.
                </p>
              </div>
              <div className="text-xs font-mono text-emerald-800 font-bold shrink-0">Live: Q3 2026</div>
            </div>

            {/* Version 1.5 */}
            <div className="p-5 bg-blue-50/50 border border-blue-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-blue-600 text-white font-mono font-bold text-xs rounded-lg">v1.5.0 NEXT</span>
                  <span className="text-xs font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded">IN DEVELOPMENT</span>
                </div>
                <h4 className="font-black text-slate-900 text-sm">Autonomous AI Copilot SOAP Generator &amp; Webcam Eye-Gaze Tracking</h4>
                <p className="text-xs text-slate-500">
                  Real-time webcam eye-gaze dwell control for AAC, automated clinical audio-to-SOAP transcription, and parent WhatsApp bot notifications.
                </p>
              </div>
              <div className="text-xs font-mono text-blue-700 font-bold shrink-0">Target: Q4 2026</div>
            </div>

            {/* Version 2.0 */}
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-purple-600 text-white font-mono font-bold text-xs rounded-lg">v2.0.0 FUTURE</span>
                  <span className="text-xs font-bold text-purple-800 bg-purple-100 px-2 py-0.5 rounded">PLANNING</span>
                </div>
                <h4 className="font-black text-slate-900 text-sm">Direct Automated NDIS EDI Claims Gateway &amp; Bionic Robotics API</h4>
                <p className="text-xs text-slate-500">
                  Instant electronic claim settlement with Australian NDIA, automated Indian UDID card registry integration, and IoT sensory room hardware controllers.
                </p>
              </div>
              <div className="text-xs font-mono text-purple-700 font-bold shrink-0">Target: Q2 2027</div>
            </div>

            {/* Version 2.5 */}
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-indigo-600 text-white font-mono font-bold text-xs rounded-lg">v2.5.0 VISION</span>
                  <span className="text-xs font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded">CONCEPT</span>
                </div>
                <h4 className="font-black text-slate-900 text-sm">Multi-Region Federated Mesh &amp; Autonomous IEP Curriculum</h4>
                <p className="text-xs text-slate-500">
                  Global data sovereignty compliance, offline-first peer-to-peer sync for rural outreach clinics, and generative AI dynamic IEP curriculum sequencing.
                </p>
              </div>
              <div className="text-xs font-mono text-indigo-700 font-bold shrink-0">Target: Q4 2027</div>
            </div>

          </div>
        </div>
      )}

      {/* TAB 6: SERVER DIAGNOSTICS & INFRASTRUCTURE HEALTH */}
      {activeTab === 'server' && (
        <div className="bg-slate-900 text-white p-6 md:p-8 rounded-3xl border border-slate-800 shadow-xl space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
            <div>
              <div className="text-xs text-emerald-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Activity className="w-4 h-4" /> cPanel &amp; WHM Infrastructure Health
              </div>
              <h2 className="text-2xl font-black mt-1">MultiPHP 8.5 &amp; AlmaLinux Server Telemetry</h2>
            </div>
            <div className="px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full font-mono text-xs font-bold self-start sm:self-auto">
              UPTIME: 99.99% &bull; SERVER OK
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
              <div className="text-xs text-slate-400 font-bold">PHP Binary Handler</div>
              <div className="text-sm font-mono text-amber-300 font-bold">/opt/cpanel/ea-php85/root/usr/bin/php</div>
              <div className="text-[11px] text-emerald-400 font-bold">Strict PHP 8.5 Enforcement (Rule Active)</div>
            </div>

            <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
              <div className="text-xs text-slate-400 font-bold">MariaDB Database Cluster</div>
              <div className="text-sm font-mono text-blue-300 font-bold">127.0.0.1:3306 (InnoDB)</div>
              <div className="text-[11px] text-emerald-400 font-bold">142 Tenant Databases Synced</div>
            </div>

            <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
              <div className="text-xs text-slate-400 font-bold">Redis Cache &amp; WebSockets</div>
              <div className="text-sm font-mono text-purple-300 font-bold">127.0.0.1:6379 (Laravel Reverb)</div>
              <div className="text-[11px] text-emerald-400 font-bold">Real-Time AAC Latency &lt; 14ms</div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 7: FEATURE FLAGS & GLOBAL KILLSWITCHES */}
      {activeTab === 'feature_flags' && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-black rounded-full uppercase tracking-wider">
                System Governance
              </span>
              <h2 className="text-2xl font-black text-slate-900 mt-2">Global Feature Flags &amp; Killswitches</h2>
              <p className="text-xs text-slate-500">Live operational switches affecting all tenants in real-time.</p>
            </div>
          </div>

          <div className="space-y-3">
            {[
              {
                id: 'aiSoapCopilot' as const,
                title: 'Autonomous AI Clinical SOAP Note Copilot',
                desc: 'Uses ambient clinical audio and therapist prompts to generate standardized SOAP progress notes.'
              },
              {
                id: 'webcamEyeGaze' as const,
                title: 'Webcam Eye-Gaze Dwell Tracking for AAC',
                desc: 'Enables real-time pupil gaze tracking with configurable 200ms–800ms dwell fixation selection.'
              },
              {
                id: 'ambientTherapyJukebox' as const,
                title: '16-Channel Solfeggio & Raga Ambient Therapy Music Engine',
                desc: 'Permits global background auditory entrainment across public and clinical workspaces.'
              },
              {
                id: 'websocketsRealTimeSync' as const,
                title: 'Laravel Reverb Real-Time WebSockets AAC Sync',
                desc: 'Broadcasts AAC speech utterances and IEP star tokens to linked parent companion screens.'
              },
              {
                id: 'teleRehabWebRtc' as const,
                title: 'Tele-Rehab WebRTC Low-Latency Video Signaling',
                desc: 'P2P encrypted video streaming for remote Speech-Language Pathology and OT consultations.'
              },
              {
                id: 'ndisAutoClaims' as const,
                title: 'Automated Australian NDIA / Indian UDID EDI Claims Gateway',
                desc: 'Direct electronic claim submission for clinical billable hours.'
              },
              {
                id: 'offlineMeshSync' as const,
                title: 'Offline-First Mesh Synchronization for Rural Clinics',
                desc: 'P2P synchronization without active internet connectivity.'
              }
            ].map((flag) => {
              const active = featureFlags[flag.id];
              return (
                <div key={flag.id} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <h4 className="font-black text-slate-900 text-sm">{flag.title}</h4>
                    <p className="text-xs text-slate-500">{flag.desc}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleFeatureFlag(flag.id)}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 shrink-0 ${
                      active 
                        ? 'bg-emerald-600 text-white shadow-sm' 
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  >
                    {active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    <span>{active ? 'ENABLED' : 'DISABLED'}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};
